import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import AchievementBadge from "@/components/badges/AchievementBadge";
import { BADGE_CATEGORIES } from "@/lib/badges";
import { Award } from "lucide-react";
import BadgeTimeline from "@/components/badges/BadgeTimeline";
import NextBadgeGoal from "@/components/badges/NextBadgeGoal";
import { useAuth } from "@/contexts/AuthContext";

export default function StudentBadges() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/badges")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <p className="text-slate-400 py-12 text-center">Chargement des badges…</p>;
  }

  const earnedCodes = new Set((data?.earned || []).map((b) => b.badge_code));
  const earnedMap = Object.fromEntries((data?.earned || []).map((b) => [b.badge_code, b]));

  const byCategory = {};
  (data?.definitions || []).forEach((def) => {
    const cat = def.category || "special";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(def);
  });

  return (
    <div data-testid="student-badges-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mes badges</h1>
          <p className="text-slate-500 mt-1">Récompenses pour ta régularité et ta progression</p>
        </div>
        <Badge variant="secondary" className="text-sm px-3 py-1 gap-1.5">
          <Award className="w-4 h-4" />
          {data?.stats?.earned_count || 0} / {data?.stats?.total_count || 0}
        </Badge>
      </div>

      <div className="mt-6">
        <NextBadgeGoal nextBadge={data?.next_badge} categories={BADGE_CATEGORIES} />
      </div>

      <div className="mt-10">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Historique</h2>
        <BadgeTimeline earned={data?.earned} categories={BADGE_CATEGORIES} userName={user?.name} sharePath="/student/badges" />
      </div>

      <div className="space-y-8 mt-10">
        {Object.entries(BADGE_CATEGORIES).map(([key, meta]) => {
          const items = byCategory[key];
          if (!items?.length) return null;
          return (
            <section key={key}>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">{meta.label}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {items.map((def) => {
                  const earned = earnedCodes.has(def.code);
                  const badge = earnedMap[def.code];
                  return (
                    <AchievementBadge
                      key={def.code}
                      title={def.title}
                      description={def.description}
                      icon={def.icon}
                      category={def.category}
                      earned={earned}
                      isNew={badge?.is_new}
                      progress={data?.progress?.[def.code]}
                      size="lg"
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}